// Dados do editor de minicursos. Cursos, etapas e alunos em andamento em tempo real
// (o bot grava progresso a todo momento); temas, pílulas e questões lidos uma vez.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { firestore } from '../../config/firebaseConfig';

const comId = (d) => ({ id: d.id, ...d.data() });

export function useDadosMinicursos(tenantId) {
  const [cursos, setCursos] = useState(null);
  const [etapasTenant, setEtapasTenant] = useState(null);
  const [andamento, setAndamento] = useState(null);
  const [base, setBase] = useState(null); // { temas, pilulas, questoes }
  const [avulsos, setAvulsos] = useState({}); // conteúdos achados fora do tenant: id -> doc | null
  const [etapasExtras, setEtapasExtras] = useState([]); // etapas do curso aberto sem tenantId
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!tenantId) return undefined;
    const falha = (e) => {
      console.error('Minicursos:', e);
      setErro('Não foi possível carregar os minicursos. Confira a conexão e recarregue a página.');
    };
    const pararCursos = onSnapshot(query(collection(firestore, 'minicursos'), where('tenantId', '==', tenantId)),
      (s) => setCursos(s.docs.map(comId)), falha);
    const pararEtapas = onSnapshot(query(collection(firestore, 'etapasMinicurso'), where('tenantId', '==', tenantId)),
      (s) => setEtapasTenant(s.docs.map(comId)), falha);
    const pararAndamento = onSnapshot(
      query(collection(firestore, 'progressoAlunos'), where('tenantId', '==', tenantId), where('concluido', '==', false)),
      (s) => setAndamento(s.docs.map(comId)), falha);
    return () => { pararCursos(); pararEtapas(); pararAndamento(); };
  }, [tenantId]);

  const carregarBase = useCallback(async () => {
    if (!tenantId) return;
    try {
      const doTenant = (nome) => getDocs(query(collection(firestore, nome), where('tenantId', '==', tenantId)));
      const [t, p, q] = await Promise.all([doTenant('temas'), doTenant('pilulas'), doTenant('questoes')]);
      setBase({
        temas: t.docs.map(comId).sort((a, b) => String(a.nome).localeCompare(String(b.nome), 'pt-BR')),
        pilulas: p.docs.map(comId),
        questoes: q.docs.map(comId),
      });
    } catch (e) {
      console.error('Minicursos (conteúdos):', e);
      setErro('Não foi possível carregar pílulas e questões.');
    }
  }, [tenantId]);

  useEffect(() => { carregarBase(); }, [carregarBase]);

  const pilulasPorId = useMemo(() => Object.fromEntries((base?.pilulas || []).map((p) => [p.id, p])), [base]);
  const questoesPorId = useMemo(() => Object.fromEntries((base?.questoes || []).map((q) => [q.id, q])), [base]);

  // O bot busca o conteúdo só pelo id: antes de dizer "apagado", confere direto no banco
  useEffect(() => {
    if (!base || !etapasTenant) return undefined;
    const faltando = [...etapasTenant, ...etapasExtras].filter((e) => {
      const mapa = e.tipoConteudo === 'pilula' ? pilulasPorId : questoesPorId;
      return e.conteudoId && !mapa[e.conteudoId] && !(e.conteudoId in avulsos);
    });
    if (!faltando.length) return undefined;
    let ativo = true;
    Promise.all(faltando.map(async (e) => {
      try {
        const s = await getDoc(doc(firestore, e.tipoConteudo === 'pilula' ? 'pilulas' : 'questoes', e.conteudoId));
        return [e.conteudoId, s.exists() ? comId(s) : null];
      } catch {
        return [e.conteudoId, null];
      }
    })).then((pares) => { if (ativo) setAvulsos((atual) => ({ ...atual, ...Object.fromEntries(pares) })); });
    return () => { ativo = false; };
  }, [base, etapasTenant, etapasExtras, pilulasPorId, questoesPorId, avulsos]);

  // conteúdo da etapa; null = apagado; undefined = ainda conferindo no banco
  const conteudoDe = useCallback((etapa) => {
    const mapa = etapa.tipoConteudo === 'pilula' ? pilulasPorId : questoesPorId;
    if (mapa[etapa.conteudoId]) return mapa[etapa.conteudoId];
    if (!etapa.conteudoId) return null;
    return etapa.conteudoId in avulsos ? avulsos[etapa.conteudoId] : undefined;
  }, [pilulasPorId, questoesPorId, avulsos]);

  // Etapas do tenant agrupadas por curso, em ordem e com o conteúdo resolvido
  const etapasPorCurso = useMemo(() => {
    const mapa = {};
    (etapasTenant || []).forEach((e) => { (mapa[e.minicursoId] = mapa[e.minicursoId] || []).push(e); });
    Object.keys(mapa).forEach((id) => {
      mapa[id] = mapa[id]
        .sort((a, b) => (Number(a.ordem) || 0) - (Number(b.ordem) || 0) || a.id.localeCompare(b.id))
        .map((e) => ({ ...e, conteudo: conteudoDe(e) }));
    });
    return mapa;
  }, [etapasTenant, conteudoDe]);

  const andamentoPorCurso = useMemo(() => {
    const mapa = {};
    (andamento || []).forEach((p) => { (mapa[p.minicursoId] = mapa[p.minicursoId] || []).push(p); });
    return mapa;
  }, [andamento]);

  // o curso aberto informa as etapas que o bot lê, para conferir conteúdos fora do tenant
  const registrarEtapas = useCallback((lista) => {
    const fora = (lista || []).filter((e) => e.tenantId !== tenantId);
    setEtapasExtras((atual) => (atual.length === 0 && fora.length === 0 ? atual : fora));
  }, [tenantId]);

  return {
    carregando: !erro && (!cursos || !etapasTenant || !andamento || !base),
    registrarEtapas,
    erro,
    cursos: cursos || [],
    temas: base?.temas || [],
    pilulas: base?.pilulas || [],
    questoes: base?.questoes || [],
    etapasPorCurso,
    andamentoPorCurso,
    conteudoDe,
  };
}

// Etapas de um curso exatamente como o bot lê: por minicursoId, em ordem
// (inclui etapas antigas que não tenham tenantId).
export function useEtapasDoCurso(cursoId, conteudoDe) {
  const [etapas, setEtapas] = useState(null);

  useEffect(() => {
    if (!cursoId) return undefined;
    setEtapas(null);
    return onSnapshot(
      query(collection(firestore, 'etapasMinicurso'), where('minicursoId', '==', cursoId), orderBy('ordem')),
      (s) => setEtapas(s.docs.map(comId)),
      (e) => {
        console.error('Etapas do minicurso:', e);
        setEtapas([]);
      },
    );
  }, [cursoId]);

  return useMemo(() => (etapas ? etapas.map((e) => ({ ...e, conteudo: conteudoDe(e) })) : null), [etapas, conteudoDe]);
}
